<script setup>
import { router, useForm } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import FormInput from '@/components/form/FormInput.vue'
import CompanyCountyDialog from '@/components/admin/CompanyCountyDialog.vue'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import { useFeature } from '@/composables/useFeature'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

const props = defineProps({
  companies: Array,
})

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()

// Per-county entitlements (behind the qv-direct-prediction release flag)
const qvDirectEnabled = useFeature('qv-direct-prediction')
const showCountyDialog = ref(false)
const countyDialogCompany = ref(null)

const openCountyDialog = (company) => {
  countyDialogCompany.value = company
  showCountyDialog.value = true
}

const form = useForm({
  company: '',
})

const isLoading = ref(false)

const showSpinner = computed(() => {
  return props.companies?.length === 0
})

const addCompany = () => {
  isLoading.value = true
  form.post(route('admin.company.store'), {
    preserveScroll: true,
    onSuccess: () => {
      form.reset()
      toast.add({
        severity: 'success',
        summary: t('messages.added'),
        detail: t('messages.admin.company.added_success_detail'),
        life: 3000,
      })
    },
    onError: () => {
      toast.add({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.admin.company.added_error_detail'),
        life: 3000,
      })
    },
    onFinally: () => {
      isLoading.value = false
    },
  })
}

const deleteCompany = async (company) => {
  let impactMessage = t('messages.admin.company.delete_confirm', { name: company.name })

  try {
    const response = await fetch(route('admin.company.delete-check', company.id))
    const counts = await response.json()

    const warnings = []
    if (counts.users > 0) {
      warnings.push(t('messages.admin.company.delete_impact_users', { count: counts.users }))
    }
    if (counts.evaluations > 0) {
      warnings.push(t('messages.admin.company.delete_impact_evaluations', { count: counts.evaluations }))
    }
    if (counts.templates > 0) {
      warnings.push(t('messages.admin.company.delete_impact_templates', { count: counts.templates }))
    }
    if (counts.transactions > 0) {
      warnings.push(t('messages.admin.company.delete_impact_transactions', { count: counts.transactions }))
    }

    if (warnings.length > 0) {
      impactMessage += '\n\n' + warnings.join('\n')
    }
  } catch {
    // Proceed with basic confirmation if check fails
  }

  confirm.require({
    message: impactMessage,
    header: t('messages.admin.company.delete_confirm_header'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: t('messages.cancel'),
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: t('messages.delete'),
      severity: 'danger',
    },
    accept: () => {
      router.delete(route('admin.company.destroy', company.id), {
        preserveScroll: true,
        onSuccess: () => {
          toast.add({
            severity: 'success',
            summary: t('messages.deleted'),
            detail: t('messages.admin.company.deleted_success_detail', { name: company.name }),
            life: 3000,
          })
        },
        onError: () => {
          toast.add({
            severity: 'error',
            summary: t('messages.error'),
            detail: t('messages.admin.company.deleted_error_detail', { name: company.name }),
            life: 3000,
          })
        },
      })
    },
  })
}

// RED-328: per-company web analytics switch, optimistic with revert
const analyticsState = ref({})
const savingAnalytics = ref(null)

const toggleAnalytics = (company, enabled) => {
  const previous = analyticsState.value[company.id] ?? company.analytics_enabled
  analyticsState.value = { ...analyticsState.value, [company.id]: enabled }
  savingAnalytics.value = company.id

  router.put(
    route('admin.company.analytics', company.id),
    { enabled },
    {
      preserveScroll: true,
      onSuccess: () => {
        toast.add({
          severity: 'success',
          summary: t('messages.updated'),
          detail: t('messages.admin.company.analytics_updated_detail'),
          life: 3000,
        })
      },
      onError: () => {
        analyticsState.value = { ...analyticsState.value, [company.id]: previous }
        toast.add({
          severity: 'error',
          summary: t('messages.error'),
          detail: t('messages.admin.company.analytics_error_detail'),
          life: 3000,
        })
      },
      onFinish: () => {
        savingAnalytics.value = null
      },
    },
  )
}

// RED-322: REdata pool for Analüütika price statistics, optimistic with revert
const dataPoolState = ref({})
const savingDataPool = ref(null)

const isRedataPool = company => (dataPoolState.value[company.id] ?? company.data_pool) === 'redata'

const toggleDataPool = (company, enabled) => {
  const previous = dataPoolState.value[company.id] ?? company.data_pool
  const pool = enabled ? 'redata' : 'company'
  dataPoolState.value = { ...dataPoolState.value, [company.id]: pool }
  savingDataPool.value = company.id

  router.put(
    route('admin.company.data-pool', company.id),
    { pool },
    {
      preserveScroll: true,
      onSuccess: () => {
        toast.add({
          severity: 'success',
          summary: t('messages.updated'),
          detail: t('messages.admin.company.data_pool_updated_detail'),
          life: 3000,
        })
      },
      onError: () => {
        dataPoolState.value = { ...dataPoolState.value, [company.id]: previous }
        toast.add({
          severity: 'error',
          summary: t('messages.error'),
          detail: t('messages.admin.company.data_pool_error_detail'),
          life: 3000,
        })
      },
      onFinish: () => {
        savingDataPool.value = null
      },
    },
  )
}

const changeCompany = () => {
  // TODO: Implement update company functionality
  console.log('not implemented')
}
</script>
<template>
  <div>
    <form class="mb-3 mt-3 flex items-start">
      <FormInput
        v-model="form.company"
        :form="form"
        field="company"
        :placeholder="$t('messages.admin.company.name')"
      />
      <Button
        class="ml-2"
        @click.stop.prevent="addCompany()"
        :disabled="!form.company || isLoading"
      >
        {{ $t('messages.admin.company.add') }}
      </Button>
    </form>
    <Card :header="$t('messages.admin.templates')">
      <template #content>
        <ProgressSpinner v-if="showSpinner"></ProgressSpinner>
        <DataTable v-else stripedRows removableSort hover :value="companies">
          <Column sortable field="name" :header="$t('messages.admin.company.name')"></Column>
          <Column sortable field="created_at" :header="$t('messages.admin.company.created')">
            <template #body="slotProps">
              {{ $filters.formatDateTime(slotProps.data.created_at) }}
            </template>
          </Column>
          <Column field="analytics_enabled">
            <template #header>
              <span class="flex items-center gap-1">
                {{ $t('messages.admin.company.analytics') }}
                <i class="pi pi-info-circle text-gray-500" v-tooltip.top="$t('messages.admin.company.analytics_hint')"></i>
              </span>
            </template>
            <template #body="slotProps">
              <ToggleSwitch
                :model-value="analyticsState[slotProps.data.id] ?? slotProps.data.analytics_enabled"
                :disabled="savingAnalytics === slotProps.data.id"
                data-testid="company-analytics-toggle"
                @update:model-value="toggleAnalytics(slotProps.data, $event)"
              />
            </template>
          </Column>
          <Column field="data_pool">
            <template #header>
              <span class="flex items-center gap-1">
                {{ $t('messages.admin.company.data_pool') }}
                <i class="pi pi-info-circle text-gray-500" v-tooltip.top="$t('messages.admin.company.data_pool_hint')"></i>
              </span>
            </template>
            <template #body="slotProps">
              <ToggleSwitch
                :model-value="isRedataPool(slotProps.data)"
                :disabled="savingDataPool === slotProps.data.id"
                data-testid="company-data-pool-toggle"
                @update:model-value="toggleDataPool(slotProps.data, $event)"
              />
            </template>
          </Column>
          <Column field="actions" header="Tegevused">
            <template #body="slotProps">
              <Button
                v-if="qvDirectEnabled"
                type="button"
                outlined
                class="mr-3"
                data-testid="company-county-open"
                :badge="String(slotProps.data.county_access?.length ?? 0)"
                @click="openCountyDialog(slotProps.data)"
              >
                {{ $t('messages.admin.countyAccess.companyButton') }}
              </Button>
              <Button
                type="button"
                outlined
                severity="danger"
                class="mr-3"
                @click="deleteCompany(slotProps.data)"
              >
                {{ $t('messages.delete') }}
              </Button>
              <!-- TODO: Implement update company functionality -->
              <!-- <Button type="button" outlined @click="changeCompany(slotProps.data)">
                {{ $t('messages.change') }}
              </Button> -->
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
    <CompanyCountyDialog
      v-model:visible="showCountyDialog"
      :company="countyDialogCompany"
    />
  </div>
</template>
